import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { Box, IconButton } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import Routes, { DELIVERY_ID_PARAM } from 'routes';
import { useDeliveries } from 'api/hooks/useDeliveries';
import { TableTypes } from 'components/ActionItemsPage/ActionItemsPage';
import DeliveryList from 'components/DeliveryListPage/components/DeliveryList/DeliveryList';
import { formatDateToYYYYMMDD, subtractBusinessDays } from 'utils/helpers/DateTimeFormat.helpers';
import { useActionItemsFilters } from './hooks/useActionItemsFilters';
import { CheckInDeliveryDialog } from 'components/CheckInDeliveryDialog/CheckInDeliveryDialog';
import { HistoricalSwitch } from 'api/models/Deliveries';
import { useDeliveryClickHandler } from 'hooks/useDeliveryClickHandler';

const dateNow = new Date();
const dateNowMinus24Hours = subtractBusinessDays(dateNow, 1);
const beginningOfTime = 0;
const formattedBeginningOfTime = formatDateToYYYYMMDD(beginningOfTime);
const formattedDateNowMinus24Hours = formatDateToYYYYMMDD(dateNowMinus24Hours);

type OverdueCheckOutDeliveriesTableProps = {
  expandedTable: TableTypes;
  handleToggleExpand: (type: TableTypes) => void;
};

const OverdueCheckOutDeliveriesTable = ({ expandedTable, handleToggleExpand }: OverdueCheckOutDeliveriesTableProps) => {
  const location = useLocation();
  const isPastDeliveries = location.pathname === Routes.PastDeliveries.path;

  const [filters, _setFilters] = useActionItemsFilters();

  const { data: overdueCheckOutDeliveries, isLoading: overdueCheckOutDeliveriesLoading } = useDeliveries({
    ...filters,
    status: 'OVERDUE_FOR_CHECK_OUT',
    lower_surgery_date: formattedBeginningOfTime,
    upper_surgery_date: formattedDateNowMinus24Hours,
    historical: HistoricalSwitch.Active,
  });

  const { activeDeliveryForCheckInDeliveryDialog, handleDeliveryClick, setActiveDeliveryForCheckInDeliveryDialog } =
    useDeliveryClickHandler();

  const navigate = useNavigate();

  const handleRowClick = useCallback(
    async (deliveryId: number) => {
      const delivery = overdueCheckOutDeliveries?.result.find(
        deliverySummary => deliverySummary.deliveryId === deliveryId,
      );

      if (!delivery) {
        throw new Error('Delivery not found');
      }

      handleDeliveryClick(deliveryId, delivery.status, !delivery.surgeryReference || !delivery.patientInitials);
    },
    [handleDeliveryClick, overdueCheckOutDeliveries?.result],
  );

  const pageSize = overdueCheckOutDeliveries?.metadata.count || 0;
  const pageSizeOptions = useMemo(() => [pageSize], [pageSize]);

  return (
    <>
      <Box
        sx={{
          backgroundColor: '#c0e6fd',
          height: '50px',
          color: '#1890ff',
          fontWeight: 'medium',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: '20px',
          padding: '5px',
        }}
      >
        <p style={{ margin: 0, paddingLeft: '10px', fontSize: '20px' }}>
          There are{' '}
          <b>
            <u style={{ fontSize: '25px', padding: '5px' }}>{overdueCheckOutDeliveries?.metadata.total || 'no'}</u>
          </b>{' '}
          Deliveries to be Checked Out {overdueCheckOutDeliveries?.metadata.total === 0 && 'today'}
        </p>
        <div>
          {overdueCheckOutDeliveries?.metadata.total !== 0 && (
            <IconButton
              name="clear"
              title="Clear"
              sx={{ paddingRight: 0 }}
              onClick={() => handleToggleExpand(TableTypes.CHECK_OUT_OVERDUE)}
              style={{ paddingRight: '10px' }}
            >
              {expandedTable == TableTypes.CHECK_OUT_OVERDUE ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
            </IconButton>
          )}
        </div>
      </Box>
      {overdueCheckOutDeliveries?.metadata.total !== 0 &&
        (expandedTable === TableTypes.CHECK_OUT_OVERDUE || expandedTable === TableTypes.ALL) && (
          <DeliveryList
            sx={{ flex: 1, overflow: 'hidden' }} // This ensures the list fills up available vertical space
            isActionItemsTable={true}
            data={overdueCheckOutDeliveries?.result || []}
            numPages={1}
            curPage={1}
            pageSizeOptions={pageSizeOptions}
            pageSize={pageSize}
            isPastDeliveries={isPastDeliveries}
            loading={overdueCheckOutDeliveriesLoading}
            onRowClick={handleRowClick}
            tableType={TableTypes.CHECK_OUT_OVERDUE}
          />
        )}

      {activeDeliveryForCheckInDeliveryDialog && (
        <CheckInDeliveryDialog
          deliveryId={activeDeliveryForCheckInDeliveryDialog.toString()}
          onClose={() => {
            setActiveDeliveryForCheckInDeliveryDialog(null);
          }}
          onCancel={() => {
            // we still want to open the delivery but not the check in workflow
            navigate(Routes.CheckIn.path.replace(DELIVERY_ID_PARAM, activeDeliveryForCheckInDeliveryDialog.toString()));
            setActiveDeliveryForCheckInDeliveryDialog(null);
          }}
        />
      )}
    </>
  );
};

export default OverdueCheckOutDeliveriesTable;
